import { FC, useRef } from 'react';
import { DataGrid, GridColDef } from '@mui/x-data-grid';
import { Box, Paper, Stack } from '@mui/material';
import dayjs from 'dayjs';
import { DatePicker, LocalizationProvider } from '@mui/x-date-pickers';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import CatchSum from '../../../Models/ReportModels/CatchSum';
import { GridApiCommunity } from '@mui/x-data-grid/internals';
import { FilterPayload } from './Filters/FilterDefinition';

interface CatchSumReportProps {
  apiRef: React.MutableRefObject<GridApiCommunity>;
  dateFrom: string;
  setDateFrom: (date: string) => void;
  dateTo: string;
  setDateTo: (date: string) => void;
  loading: boolean;
  catchSum: CatchSum[];
  fetchCatchSum: (
    filterModel?: FilterPayload,
    dateFrom?: string,
    dateTo?: string
  ) => Promise<void>;
}

const formatDate = (date: dayjs.Dayjs) => date.format('DD.MM.YYYY');

const CatchSumReport: FC<CatchSumReportProps> = (props) => {

  const gridRef = useRef<HTMLDivElement>(null);

  const columns: GridColDef[] = [
    {
      field: 'catchName',
      headerName: 'Tegund',
      flex: 1,
      minWidth: 120,
      sortable: true,
    },
    {
      field: 'amount',
      headerName: 'Magn',
      flex: 1,
      minWidth: 120,
      sortable: true,
      type: 'number',
    },
    // Hidden filterable fields
    {
      field: 'shipRegistrationNumber',
      headerName: 'Skipaskrárnúmer',
      filterable: true,
      sortable: true,
    },
    {
      field: 'shipNameSum',
      headerName: 'Skip heiti',
      filterable: true,
      sortable: true,
    },
    {
      field: 'buyerNameSum',
      headerName: 'Kaupandi nafn',
      filterable: true,
      sortable: true,
    },
    {
      field: 'buyerSSN',
      headerName: 'Kaupandi kennitala',
      filterable: true,
      sortable: true,
    },
    {
      field: 'receiverNameSum',
      headerName: 'Viðtakandi nafn',
      filterable: true,
      sortable: true,
    },
    {
      field: 'receiverSSN',
      headerName: 'Viðtakandi kennitala',
      filterable: true,
      sortable: true,
    },
    {
      field: 'sellerNameSum',
      headerName: 'Seljandi nafn',
      filterable: true,
      sortable: true,
    },
    {
      field: 'sellerSSN',
      headerName: 'Seljandi kennitala',
      filterable: true,
      sortable: true,
    }
  ];

  return (
    <LocalizationProvider dateAdapter={AdapterDayjs} adapterLocale="is">
      <Paper elevation={3} sx={{ p: 3, height: '100%', display: 'flex', flexDirection: 'column' }}>
        <Box sx={{ mb: 2 }}>
          <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2} alignItems="center">
            <DatePicker
              label="Frá"
              value={dayjs(props.dateFrom, 'DD.MM.YYYY')}
              onChange={date => {
                if (date) props.setDateFrom(formatDate(date));
              }}
              disableFuture
              slotProps={{
                textField: {
                  size: "small",
                  sx: { minWidth: 150 }
                }
              }}
            />
            <DatePicker
              label="Til"
              value={dayjs(props.dateTo, 'DD.MM.YYYY')}
              onChange={date => {
                if (date) props.setDateTo(formatDate(date));
              }}
              disableFuture
              slotProps={{
                textField: {
                  size: "small",
                  sx: { minWidth: 150 }
                }
              }}
            />
          </Stack>
        </Box >
        <Box sx={{ flexGrow: 1, height: '100%', minHeight: '400px' }}>
          <div ref={gridRef} style={{ height: '100%' }}>
            <DataGrid
              apiRef={props.apiRef}
              rows={props.catchSum}
              columns={columns}
              loading={props.loading}
              getRowId={(row) => row.catchName + row.amount}
              disableColumnFilter
              columnVisibilityModel={{shipRegistrationNumber: false, shipNameSum: false, buyerNameSum: false, buyerSSN: false, receiverNameSum: false, receiverSSN: false, sellerNameSum: false, sellerSSN: false}}
              density="standard"
              getRowHeight={() => 'auto'}
              initialState={{
                sorting: {
                  sortModel: [{ field: 'amount', sort: 'desc' }],
                },
              }}
              sx={{
                '& .MuiDataGrid-paper': {
                  minWidth: 380,
                  maxWidth: 420,
                },
                '& .MuiDataGrid-filterForm': {
                  flexDirection: 'column',
                  alignItems: 'stretch',
                  width: '100%',
                  gap: 2,
                },
              }}
              localeText={{
                toolbarFilters: "Síur",
                toolbarFiltersTooltipHide: "Fela síur",
                toolbarFiltersTooltipShow: "Sýna síur",
                columnHeaderFiltersLabel: "Sía eftir",
                filterOperatorContains: "Inniheldur",
                filterOperatorEquals: "Jafnt og",
                filterOperatorStartsWith: "Byrjar á",
                filterOperatorEndsWith: "Endar á",
                filterOperatorIs: "Er",
                filterOperatorNot: "Er ekki",
                filterOperatorIsEmpty: "Er tómt",
                filterOperatorIsNotEmpty: "Er ekki tómt",
                filterValueAny: "(einhver)",
                filterValueTrue: "satt",
                filterValueFalse: "ósatt",
                filterOperatorIsAnyOf: "Er einhver af",
                toolbarColumns: "Dálkar",
                toolbarColumnsLabel: "Dálkar",
                columnsPanelTextFieldLabel: "Finna dálk",
                columnsPanelTextFieldPlaceholder: "Leita að dálki",
                columnsPanelDragIconLabel: "Endurraða dálkum",
                columnsPanelShowAllButton: "Sýna alla",
                columnsPanelHideAllButton: "Fela alla",
                filterPanelColumns: "Dálkar",
                filterPanelOperator: "Skilyrði",
                filterPanelInputLabel: "Gildi",
                filterPanelInputPlaceholder: "Sía gildi",
                filterPanelAddFilter: "Bæta við síu",
                filterPanelDeleteIconLabel: "Eyða",
                columnMenuLabel: "Valmynd",
                columnMenuShowColumns: "Sýna dálka",
                columnMenuFilter: "Sía",
                columnMenuHideColumn: "Fela",
                columnMenuUnsort: "Hætta að raða",
                columnMenuSortAsc: "Raða hækkandi",
                columnMenuSortDesc: "Raða lækkandi",
                columnMenuManageColumns: "Stjórna dálkum",
              }}
            />
          </div>
        </Box>
      </Paper >
    </LocalizationProvider>
  );
};

export default CatchSumReport;